import { queryOptions, useQuery } from "@tanstack/react-query";
import type { GraphQLClient } from "graphql-request";
import { gql } from "graphql-request";
import type { EventEvolutionData } from "@nexus/widgets";
import { useGraphqlClient } from "@/lib/graphqlClient";
import { LIVE_QUERY_STALE_TIME_MS } from "@/hooks/dashboard/useAutoRefresh";

export interface EventTrendGroup {
  id: string;
  label: string;
  engineIds: number[];
  filterDimension: "detection_domain" | "infringement_type";
}

export const EVENT_TREND_GROUPS: EventTrendGroup[] = [
  {
    id: "piracy-monitoring",
    label: "Piracy Monitoring",
    engineIds: [3, 4, 5, 7, 9],
    filterDimension: "detection_domain",
  },
  {
    id: "cdn-sa",
    label: "CDN / SA",
    engineIds: [1, 6, 8],
    filterDimension: "infringement_type",
  },
];

export interface EventTrendChannel {
  protectedEventId: number;
  channel: string;
  tenantLabel: string;
  unresolvedAnomalyCount: number;
  groups: Array<{ groupId: string; evolution: EventEvolutionData }>;
}

const EVENT_EVOLUTION_FIELDS = `
  protected_event_id
  event_id
  event_label
  event_type
  event_start
  event_end
  blocking_start
  blocking_end
  detection_start
  response_start
  impact_start
  engine_ids
  range_from
  range_to
  interval_minutes
  detections { bucket value cumulative }
  responses { bucket value cumulative }
  impacts { bucket value cumulative }
  expected_isps
  breakdown {
    value
    anomaly_count
    expected_detection_count
    harvesting_start
    anomalies
    anomaly_details {
      anomaly_id
      raised_at
      anomaly_type
      anomaly_severity
      anomaly_status
      anomaly_description
    }
    detections { bucket count }
    confirmed_responses { bucket count }
    impacts_confirmed { bucket count }
    monitoring_engines { engine_id engine_label }
  }
`;

const EVENT_MONITORING_FIELDS = `
  protected_event_id
  blocking_start
  blocking_end
  detection_start
  response_start
  interval_minutes
  detections { bucket value cumulative }
  responses { bucket value cumulative }
  expected_isps
  breakdown {
    expected_detection_count
    anomaly_details {
      anomaly_id
      raised_at
      anomaly_type
      anomaly_severity
      anomaly_status
      anomaly_description
    }
    monitoring_engines { engine_id engine_label }
  }
`;

function eventTrendsQuery(evolutionFields: string) {
  return gql`
  query EventTrends(
    $event_id: String!
    $from_ts: String!
    $to_ts: String!
    $tenant_ids: [Int!]
    $groups: [EventEvolutionGroupInput!]!
  ) {
    event_evolution_multi(
      event_id: $event_id
      from_ts: $from_ts
      to_ts: $to_ts
      tenant_ids: $tenant_ids
      groups: $groups
    ) {
      protected_event_id
      channel
      tenant_label
      unresolved_anomaly_count
      groups {
        engine_ids
        filter_dimension
        evolution { ${evolutionFields} }
      }
    }
  }
`;
}

const EVENT_TRENDS_QUERY = eventTrendsQuery(EVENT_EVOLUTION_FIELDS);
const EVENT_MONITORING_QUERY = eventTrendsQuery(EVENT_MONITORING_FIELDS);

type EventTrendsResponse = {
  event_evolution_multi: Array<{
    protected_event_id: number;
    channel: string;
    tenant_label: string;
    unresolved_anomaly_count: number;
    groups: Array<{
      engine_ids: number[];
      filter_dimension: string | null;
      evolution: EventTrendEvolutionResponse;
    }>;
  }>;
};

interface EventTrendEvolutionResponse {
  protected_event_id: number;
  event_id?: string | null;
  event_label?: string;
  event_type?: string;
  event_start?: string | null;
  event_end?: string | null;
  blocking_start: string | null;
  blocking_end: string | null;
  detection_start: string | null;
  response_start: string | null;
  impact_start?: string | null;
  engine_ids?: number[];
  range_from?: string;
  range_to?: string;
  interval_minutes: number;
  detections: Array<{ bucket: string; value: number; cumulative: number }>;
  responses: Array<{ bucket: string; value: number; cumulative: number }>;
  impacts?: Array<{ bucket: string; value: number; cumulative: number }>;
  expected_isps: number;
  breakdown: Array<{
    value?: string;
    anomaly_count?: number;
    expected_detection_count: number;
    harvesting_start?: string | null;
    anomalies?: string[];
    anomaly_details: EventEvolutionData["breakdown"][number]["anomaly_details"];
    detections?: Array<{ bucket: string; count: number }>;
    confirmed_responses?: Array<{ bucket: string; count: number }>;
    impacts_confirmed?: Array<{ bucket: string; count: number }>;
    monitoring_engines: Array<{ engine_id: number; engine_label: string }>;
  }>;
}

export function normalizeEventTrendEvolution(raw: EventTrendEvolutionResponse): EventEvolutionData {
  return {
    protected_event_id: Number(raw.protected_event_id),
    event_id: raw.event_id ?? null,
    event_label: raw.event_label ?? "",
    event_type: raw.event_type ?? "",
    event_start: raw.event_start ?? null,
    event_end: raw.event_end ?? null,
    blocking_start: raw.blocking_start,
    blocking_end: raw.blocking_end,
    detection_start: raw.detection_start,
    response_start: raw.response_start,
    impact_start: raw.impact_start ?? null,
    engine_ids: (raw.engine_ids ?? []).map(Number),
    range_from: raw.range_from ?? "",
    range_to: raw.range_to ?? "",
    interval_minutes: Number(raw.interval_minutes ?? 0),
    expected_isps: Number(raw.expected_isps ?? 1),
    detections: (raw.detections ?? []).map((point) => ({
      timestamp: point.bucket,
      value: Number(point.value),
      cumulative: Number(point.cumulative),
    })),
    responses: (raw.responses ?? []).map((point) => ({
      timestamp: point.bucket,
      value: Number(point.value),
      cumulative: Number(point.cumulative),
    })),
    impacts: (raw.impacts ?? []).map((point) => ({
      timestamp: point.bucket,
      value: Number(point.value),
      cumulative: Number(point.cumulative),
    })),
    breakdown: (raw.breakdown ?? []).map((entry) => ({
      value: entry.value ?? "",
      anomaly_count: Number(entry.anomaly_count ?? 0),
      expected_detection_count: Number(entry.expected_detection_count),
      harvesting_start: entry.harvesting_start ?? null,
      anomalies: entry.anomalies ?? [],
      anomaly_details: entry.anomaly_details ?? [],
      detections: (entry.detections ?? []).map((point) => ({
        timestamp: point.bucket,
        count: Number(point.count),
      })),
      confirmed_responses: (entry.confirmed_responses ?? []).map((point) => ({
        timestamp: point.bucket,
        count: Number(point.count),
      })),
      impacts_confirmed: (entry.impacts_confirmed ?? []).map((point) => ({
        timestamp: point.bucket,
        count: Number(point.count),
      })),
      monitoring_engines: (entry.monitoring_engines ?? []).map((engine) => ({
        engine_id: Number(engine.engine_id),
        engine_label: engine.engine_label,
      })),
    })),
  };
}

function normalizeChannels(raw: EventTrendsResponse): EventTrendChannel[] {
  return (raw.event_evolution_multi ?? []).map((channel) => ({
    protectedEventId: Number(channel.protected_event_id),
    channel: channel.channel,
    tenantLabel: channel.tenant_label,
    unresolvedAnomalyCount: Number(channel.unresolved_anomaly_count),
    groups: (channel.groups ?? []).flatMap((group) => {
      const matchedGroup = EVENT_TREND_GROUPS.find(
        (definition) => definition.filterDimension === group.filter_dimension,
      );
      return matchedGroup
        ? [{ groupId: matchedGroup.id, evolution: normalizeEventTrendEvolution(group.evolution) }]
        : [];
    }),
  }));
}

export function eventTrendsQueryOptions(
  client: GraphQLClient,
  input: {
    eventId: string | null;
    from: string;
    to: string;
    tenantIds: string[];
  },
) {
  const numericTenantIds = input.tenantIds.map(Number);
  return queryOptions({
    queryKey: ["event-trends", input.eventId, input.from, input.to, numericTenantIds],
    enabled: Boolean(input.eventId),
    queryFn: ({ signal }) =>
      fetchEventTrends(
        client,
        {
          eventId: input.eventId!,
          from: input.from,
          to: input.to,
          tenantIds: numericTenantIds,
        },
        EVENT_TRENDS_QUERY,
        signal,
      ),
    staleTime: LIVE_QUERY_STALE_TIME_MS,
  });
}

export function useEventTrendsData(input: {
  eventId: string | null;
  from: string;
  to: string;
  tenantIds: string[];
}) {
  const client = useGraphqlClient();
  return useQuery(eventTrendsQueryOptions(client, input));
}

export async function fetchEventTrends(
  client: GraphQLClient,
  input: { eventId: string; from: string; to: string; tenantIds: number[] },
  query = EVENT_TRENDS_QUERY,
  signal?: AbortSignal,
) {
  const variables = {
    event_id: input.eventId,
    from_ts: input.from,
    to_ts: input.to,
    tenant_ids: input.tenantIds.length ? input.tenantIds : undefined,
    groups: EVENT_TREND_GROUPS.map((group) => ({
      engine_ids: group.engineIds,
      filter_dimension: group.filterDimension,
    })),
  };
  const raw = signal
    ? await client.request<EventTrendsResponse>({ document: query, variables, signal })
    : await client.request<EventTrendsResponse>(query, variables);
  return normalizeChannels(raw);
}

export function fetchEventMonitoringTrends(
  client: GraphQLClient,
  input: { eventId: string; from: string; to: string; tenantIds: number[] },
) {
  return fetchEventTrends(client, input, EVENT_MONITORING_QUERY);
}
